import { useEffect, useMemo, useState } from 'react'
import usePersistedState from '../../hooks/usePersistedState.js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createReferral } from '../../api/encounters.js'
import { listFacilitySpecialties } from '../../api/catalogues.js'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { Card, CardHeader } from '../../components/Card.jsx'
import {
  URGENCY,
  URGENCY_DEFAULT,
  FACILITY_TYPES,
  FACILITY_TYPE_LABEL,
  CADRES,
  CADRE_LABEL,
} from '../../constants/index.js'
import { statusColor } from '../../utils/format.js'
import FacilityAutocomplete from './FacilityAutocomplete.jsx'

function urgencyClass(u) {
  if (u === 'emergency') return 'bg-rose-50 text-rose-700 border-rose-200'
  if (u === 'urgent') return 'bg-amber-50 text-amber-700 border-amber-200'
  return 'bg-slate-100 text-slate-600 border-slate-200'
}

function formatWhen(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return ''
  }
}

// Small two-column row for read-only display in the receipt.
function KV({ label, value }) {
  if (!value) return null
  return (
    <div className="flex gap-2 text-xs text-slate-700 leading-snug">
      <span className="uppercase tracking-wide text-slate-400 text-[10px] shrink-0 w-28 pt-[1px]">
        {label}
      </span>
      <span className="min-w-0 break-words whitespace-pre-wrap">{value}</span>
    </div>
  )
}

/**
 * Receipt-style card for a single persisted referral. Renders everything
 * the doctor just filled in so that after "Add referral" the form clears
 * and the data shows up above as a completed record.
 */
function ReferralReceipt({ referral, isNew }) {
  const f = referral.to_facility_detail
  const fromFacTypeLbl =
    referral.from_facility_type_display || FACILITY_TYPE_LABEL[referral.from_facility_type] || ''
  const fromCadreLbl =
    referral.from_cadre_display || CADRE_LABEL[referral.from_cadre] || ''
  const toFacTypeLbl =
    referral.to_facility_type_display ||
    FACILITY_TYPE_LABEL[referral.to_facility_type] ||
    FACILITY_TYPE_LABEL[f?.facility_type] ||
    ''
  const toCadreLbl =
    referral.to_cadre_display || CADRE_LABEL[referral.to_cadre] || ''

  return (
    <div
      className={`rounded-md border text-sm p-3 mb-2 transition-colors ${
        isNew
          ? 'border-wantok-300 bg-wantok-50 ring-1 ring-wantok-200'
          : 'border-slate-200 bg-white'
      }`}
    >
      {/* Header row — status / urgency / flash / timestamp */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span
          className={`px-2 py-0.5 rounded-full border text-[10px] uppercase ${statusColor(
            referral.status,
          )}`}
        >
          {referral.status}
        </span>
        <span
          className={`px-2 py-0.5 rounded-full border text-[10px] uppercase ${urgencyClass(
            referral.urgency,
          )}`}
        >
          {referral.urgency}
        </span>
        {isNew && (
          <span className="px-2 py-0.5 rounded-full border border-wantok-300 bg-white text-wantok-700 text-[10px] uppercase">
            Just submitted
          </span>
        )}
        <span className="ml-auto text-[11px] text-slate-400">
          {formatWhen(referral.created_at)}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Referring from */}
        <div className="rounded border border-slate-200 bg-slate-50 p-2.5">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">
            Referring from
          </div>
          <div className="space-y-1">
            <KV label="Facility type" value={fromFacTypeLbl} />
            <KV label="Cadre" value={fromCadreLbl} />
            <KV label="Specialty" value={referral.from_specialty} />
            <KV label="Address" value={referral.from_address} />
            {referral.from_doctor_name && (
              <KV label="Clinician" value={referral.from_doctor_name} />
            )}
          </div>
        </div>

        {/* Referring to */}
        <div className="rounded border border-wantok-200 bg-wantok-50/60 p-2.5">
          <div className="text-[10px] uppercase tracking-wide text-wantok-700 mb-1.5">
            Referring to
          </div>
          <div className="space-y-1">
            <KV label="Facility type" value={toFacTypeLbl} />
            {f ? (
              <>
                <KV label="Facility" value={f.name} />
                {f.town && <KV label="Town" value={f.town} />}
                {f.phone_number && <KV label="Phone" value={f.phone_number} />}
              </>
            ) : (
              referral.to_facility_name && (
                <KV label="Facility" value={referral.to_facility_name} />
              )
            )}
            <KV label="Cadre" value={toCadreLbl} />
            <KV
              label="Specialty"
              value={referral.specialty || f?.specialty || ''}
            />
          </div>
        </div>
      </div>

      {/* Reason — spans full width under the two columns */}
      {referral.reason && (
        <div className="mt-3 rounded border border-slate-200 bg-white p-2.5">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
            Reason for referral
          </div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap">
            {referral.reason}
          </div>
        </div>
      )}
    </div>
  )
}

// Tiny labelled <select> wrapper to keep the form body compact.
function LabeledSelect({ label, value, onChange, options, placeholder = '— Select —' }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-white"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function LabeledInput({ label, value, onChange, placeholder = '' }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
      />
    </label>
  )
}

export default function ReferralForm({ encounter, locked }) {
  const qc = useQueryClient()

  // Persist transient draft to localStorage so half-built referrals
  // survive HMR / tab close / re-mounts. Cleared on successful submit.
  const draftKey = `ref:enc${encounter.id}`

  // ---- Referring-from state ------------------------------------------------
  const [fromFacilityType, setFromFacilityType] = usePersistedState(`${draftKey}:fromFacility`, '')
  const [fromCadre, setFromCadre] = usePersistedState(`${draftKey}:fromCadre`, '')
  const [fromAddress, setFromAddress] = usePersistedState(`${draftKey}:fromAddress`, '')
  const [fromSpecialty, setFromSpecialty] = usePersistedState(`${draftKey}:fromSpecialty`, '')

  // ---- Referring-to state --------------------------------------------------
  const [toFacilityType, setToFacilityType] = usePersistedState(`${draftKey}:toFacility`, '')
  const [toCadre, setToCadre] = usePersistedState(`${draftKey}:toCadre`, '')
  const [selectedFacility, setSelectedFacility] = usePersistedState(`${draftKey}:facility`, null)
  const [toSpecialty, setToSpecialty] = usePersistedState(`${draftKey}:toSpecialty`, '')

  // ---- Clinical state ------------------------------------------------------
  const [reason, setReason] = usePersistedState(`${draftKey}:reason`, '')
  const [urgency, setUrgency] = usePersistedState(`${draftKey}:urgency`, URGENCY_DEFAULT)
  const [formError, setFormError] = useState(null)

  // Flash highlight the most-recently-created referral for 6s
  const [justCreatedId, setJustCreatedId] = useState(null)
  useEffect(() => {
    if (justCreatedId == null) return
    const t = setTimeout(() => setJustCreatedId(null), 6000)
    return () => clearTimeout(t)
  }, [justCreatedId])

  const { data: specialties = [] } = useQuery({
    queryKey: ['facility-specialties'],
    queryFn: listFacilitySpecialties,
    staleTime: 10 * 60_000,
  })

  // When a facility is picked, pre-fill to_specialty if the facility has one
  // and the doctor hasn't typed anything yet.
  useEffect(() => {
    if (selectedFacility?.specialty && !toSpecialty) {
      setToSpecialty(selectedFacility.specialty)
    }
  }, [selectedFacility]) // eslint-disable-line react-hooks/exhaustive-deps

  const mut = useMutation({
    mutationFn: (body) => createReferral(encounter.id, body),
    onSuccess: (created) => {
      // Splice the new referral into the encounter cache directly from
      // the POST response so the row appears instantly without waiting
      // for an invalidate + refetch round-trip.
      qc.setQueryData(['encounter', encounter.id], (prev) => {
        if (!prev || !created) return prev
        return { ...prev, referrals: [...(prev.referrals || []), created] }
      })
      qc.invalidateQueries({ queryKey: ['encounter', encounter.id] })
      // Clear the form
      setFromFacilityType('')
      setFromCadre('')
      setFromAddress('')
      setFromSpecialty('')
      setToFacilityType('')
      setToCadre('')
      setSelectedFacility(null)
      setToSpecialty('')
      setReason('')
      setUrgency('routine')
      setFormError(null)
      if (created?.id) setJustCreatedId(created.id)
    },
  })

  function onSubmit(e) {
    e.preventDefault()
    setFormError(null)
    // Autocomplete is authoritative for the specific receiving facility,
    // but we also accept a cadre-at-facility-type-only referral (rare —
    // "any MO at any Health Centre") as long as the type + reason are set.
    if (!selectedFacility && !toFacilityType) {
      return setFormError(
        'Pick a receiving facility or at least a facility type.',
      )
    }
    if (!reason.trim()) return setFormError('Enter a reason for referral.')

    mut.mutate({
      // Referring from
      from_facility_type: fromFacilityType,
      from_cadre: fromCadre,
      from_address: fromAddress,
      from_specialty: fromSpecialty,
      // Referring to
      to_facility: selectedFacility?.id || null,
      to_facility_type: toFacilityType || selectedFacility?.facility_type || '',
      to_cadre: toCadre,
      specialty: toSpecialty || selectedFacility?.specialty || '',
      // Clinical
      reason,
      urgency,
    })
  }

  // Newest referral first, with the just-created one visually highlighted.
  const sortedReferrals = useMemo(() => {
    const rows = [...(encounter.referrals || [])]
    rows.sort((a, b) => {
      const ta = a.created_at ? Date.parse(a.created_at) : 0
      const tb = b.created_at ? Date.parse(b.created_at) : 0
      if (tb !== ta) return tb - ta
      return (b.id || 0) - (a.id || 0)
    })
    return rows
  }, [encounter.referrals])

  return (
    <Card>
      <CardHeader
        title="Referrals"
        subtitle="Structured hand-off — who is referring, where to, and why"
      />

      {/* Completed referrals — receipt cards, newest first */}
      {sortedReferrals.length > 0 && (
        <div className="mb-4">
          {sortedReferrals.map((r) => (
            <ReferralReceipt
              key={r.id}
              referral={r}
              isNew={r.id === justCreatedId}
            />
          ))}
        </div>
      )}

      {locked ? (
        <div className="text-xs text-slate-400">
          {sortedReferrals.length === 0
            ? 'No referrals were recorded on this encounter.'
            : 'Encounter is locked. Add new referrals via an amended encounter.'}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          {/* =========================== Referring from ==================== */}
          <fieldset className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <legend className="px-1 text-[11px] uppercase tracking-wide text-slate-500">
              Referring from
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <LabeledSelect
                label="Facility type"
                value={fromFacilityType}
                onChange={setFromFacilityType}
                options={FACILITY_TYPES}
              />
              <LabeledSelect
                label="Cadre"
                value={fromCadre}
                onChange={setFromCadre}
                options={CADRES}
              />
              <LabeledInput
                label="Specialty"
                value={fromSpecialty}
                onChange={setFromSpecialty}
                placeholder="e.g. General Medicine"
              />
              <LabeledInput
                label="Address"
                value={fromAddress}
                onChange={setFromAddress}
                placeholder="Street, town, province"
              />
            </div>
          </fieldset>

          {/* =========================== Referring to ====================== */}
          <fieldset className="rounded-md border border-wantok-200 bg-wantok-50/50 p-3">
            <legend className="px-1 text-[11px] uppercase tracking-wide text-wantok-700">
              Referring to
            </legend>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <LabeledSelect
                label="Facility type"
                value={toFacilityType}
                onChange={(v) => {
                  setToFacilityType(v)
                  // If the user narrows the type while a different-typed
                  // facility is selected, drop the stale selection.
                  if (
                    selectedFacility &&
                    v &&
                    selectedFacility.facility_type !== v
                  ) {
                    setSelectedFacility(null)
                  }
                }}
                options={FACILITY_TYPES}
              />
              <LabeledSelect
                label="Cadre to receive"
                value={toCadre}
                onChange={setToCadre}
                options={CADRES}
              />
            </div>

            <div className="mt-2">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-slate-500">
                  Receiving facility
                </span>
                <div className="mt-1">
                  <FacilityAutocomplete
                    facilityType={toFacilityType}
                    specialty={''}
                    value={selectedFacility?.id || null}
                    selected={selectedFacility}
                    onPick={(f) => {
                      setSelectedFacility(f)
                      // Snap facility type to whatever the directory says.
                      if (f?.facility_type) setToFacilityType(f.facility_type)
                    }}
                    onClear={() => setSelectedFacility(null)}
                  />
                </div>
              </label>
            </div>

            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-slate-500">
                  Specialty requested
                </span>
                <input
                  list="referral-specialty-suggestions"
                  value={toSpecialty}
                  onChange={(e) => setToSpecialty(e.target.value)}
                  className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                  placeholder={
                    selectedFacility?.specialty
                      ? `Defaults to "${selectedFacility.specialty}"`
                      : 'e.g. Infectious Disease'
                  }
                />
                {specialties.length > 0 && (
                  <datalist id="referral-specialty-suggestions">
                    {specialties.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                )}
              </label>
              <LabeledSelect
                label="Urgency"
                value={urgency}
                onChange={setUrgency}
                options={URGENCY.map((u) => ({ value: u, label: u }))}
                placeholder=""
              />
            </div>

            <label className="block mt-2">
              <span className="text-[11px] uppercase tracking-wide text-slate-500">
                Reason for referral
              </span>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                placeholder="Clinical context, pertinent findings, what the receiving clinician should address…"
              />
            </label>
          </fieldset>

          {(formError || mut.error) && (
            <ErrorBanner error={formError || mut.error} />
          )}

          <div className="flex justify-end">
            <button
              disabled={mut.isPending}
              className="bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 text-sm disabled:opacity-60"
            >
              {mut.isPending ? 'Saving…' : 'Add referral'}
            </button>
          </div>
        </form>
      )}
    </Card>
  )
}
